import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoFiltersCard from "../../../components/eco-ui/EcoFiltersCard";
import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { HOMEPAGE } from "../../home/routes";
import { nr1HubFromToken } from "../nr1Hub";
import { NR1_LINKS_ADMIN_PAGE } from "../routes";

import { getMassBalanceStoresApi, type StoreShort } from "../../estoque/api/massBalanceApi";
import { listEvaluationFormsApi, type EvaluationForm } from "../../evaluations/evaluationFormsApi";
import { useGoBack } from "../../../hooks/useGoBack";
import {
  getNr1DimensionSummaryApi,
  getNr1EvolutionApi,
  getNr1ParticipationApi,
  getNr1QuestionBreakdownApi,
  type Nr1DimensionSummary,
  type Nr1EvolutionPoint,
  type Nr1Participation,
  type Nr1QuestionBreakdown,
  type Nr1StoreSummary,
} from "../nr1Api";

import "./Nr1Pages.css";

// ---------------------------------------------------------------------------
// Faixas e cores
// ---------------------------------------------------------------------------

type Level = "BAIXO" | "MEDIO" | "ALTO";

/** Mesma regra do backend: até 1/3 baixo, até 2/3 médio, acima alto. */
const levelOf = (risk01: number): Level => (risk01 < 1 / 3 ? "BAIXO" : risk01 < 2 / 3 ? "MEDIO" : "ALTO");

const LEVEL_LABEL: Record<string, string> = { BAIXO: "Baixo", MEDIO: "Médio", ALTO: "Alto" };
const LEVEL_CLASS: Record<string, string> = { BAIXO: "good", MEDIO: "warn", ALTO: "bad" };

/** Sequencial de um matiz (vermelho) para magnitude de risco no mapa — o mesmo de antes. */
function riskCellColor(risk01: number): { bg: string; ink: string } {
  const t = Math.min(1, Math.max(0, risk01));
  const from = { r: 0xfd, g: 0xec, b: 0xea };
  const to = { r: 0x99, g: 0x1b, b: 0x1b };
  const r = Math.round(from.r + (to.r - from.r) * t);
  const g = Math.round(from.g + (to.g - from.g) * t);
  const b = Math.round(from.b + (to.b - from.b) * t);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return { bg: `rgb(${r}, ${g}, ${b})`, ink: luminance > 0.6 ? "#0e1f14" : "#ffffff" };
}

/**
 * Séries da evolução: geral em tinta, as duas dimensões em ordem fixa (azul,
 * âmbar). Par validado com o validador de paleta da skill de dataviz (CVD e
 * contraste ok) e diferente do vermelho reservado ao status "alto".
 */
const EVOLUTION_COLORS = ["#0e1f14", "#2b6cb0", "#c77d14"];

const pct = (v: number | null | undefined) => (v == null ? "—" : `${Math.round(v * 100)}%`);

const monthLabel = (month: string, style: "short" | "long") => {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return month;
  const text = new Intl.DateTimeFormat("pt-BR", { month: style }).format(new Date(y, m - 1, 1)).replace(".", "");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const LevelPill: React.FC<{ level: string }> = ({ level }) => (
  <span className={`nr1Level nr1Level--${LEVEL_CLASS[level] ?? "warn"}`}>{LEVEL_LABEL[level] ?? level}</span>
);

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

const PREVIEW_QUESTIONS = 5;
const PREVIEW_COMMENTS = 3;

const Nr1DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const hub = nr1HubFromToken(token);
  const goBack = useGoBack(hub.to);

  const [stores, setStores] = useState<StoreShort[]>([]);
  const [forms, setForms] = useState<EvaluationForm[]>([]);

  const [formId, setFormId] = useState("");
  const [storeId, setStoreId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [summary, setSummary] = useState<Nr1DimensionSummary>({ dimensions: [], stores: [] });
  const [participation, setParticipation] = useState<Nr1Participation[]>([]);
  const [breakdown, setBreakdown] = useState<Nr1QuestionBreakdown[]>([]);
  const [evolution, setEvolution] = useState<Nr1EvolutionPoint[]>([]);
  const [compareStoreId, setCompareStoreId] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [storesData, formsData] = await Promise.all([
          getMassBalanceStoresApi(token),
          listEvaluationFormsApi({}),
        ]);
        setStores(storesData || []);
        setForms((formsData || []).filter((f) => f.category === "NR1"));
      } catch {
        toast.error("Erro ao carregar lojas/formulários.");
      }
    })();
  }, [token]);

  const loadReports = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const params = {
      formId: formId || undefined,
      storeId: storeId || undefined,
      from: from || undefined,
      to: to || undefined,
    };
    try {
      const [summaryData, participationData, breakdownData, evolutionData] = await Promise.all([
        getNr1DimensionSummaryApi(params).catch(() => ({ dimensions: [], stores: [] })),
        getNr1ParticipationApi(params).catch(() => []),
        getNr1QuestionBreakdownApi(params).catch(() => []),
        getNr1EvolutionApi({ ...params, months: 12 }).catch(() => []),
      ]);
      setSummary(summaryData);
      setParticipation(participationData);
      setBreakdown(breakdownData);
      setEvolution(evolutionData);
    } catch {
      toast.error("Erro ao carregar os relatórios NR1.");
    } finally {
      setLoading(false);
    }
  }, [token, formId, storeId, from, to]);

  // Recarrega a cada mudança de filtro (antes era pelo botão "Aplicar filtros").
  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleClear = () => {
    setFormId("");
    setStoreId("");
    setFrom("");
    setTo("");
  };

  // ── Ranking (mais crítica primeiro) ────────────────────────────────────────
  const ranking = useMemo(
    () => summary.stores.slice().sort((a, b) => b.overallRisk01 - a.overallRisk01),
    [summary],
  );

  // Comparação: começa na loja mais crítica; mantém a escolhida se ela continuar no filtro.
  useEffect(() => {
    setCompareStoreId((prev) => (prev && ranking.some((s) => s.storeId === prev) ? prev : ranking[0]?.storeId ?? ""));
  }, [ranking]);

  // ── Média ponderada por dimensão ───────────────────────────────────────────
  const dimensionAverages = useMemo(() => {
    const map: Record<string, number | null> = {};
    summary.dimensions.forEach((dim) => {
      const all = summary.stores
        .map((s) => s.dimensions.find((d) => d.dimension === dim))
        .filter(Boolean) as { risk01: number; answersCount: number }[];
      const n = all.reduce((s, d) => s + d.answersCount, 0);
      map[dim] = n > 0 ? all.reduce((s, d) => s + d.risk01 * d.answersCount, 0) / n : null;
    });
    return map;
  }, [summary]);

  // ── Indicadores ────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const totalResponses = summary.stores.reduce((s, x) => s + x.responses, 0);
    const totalLinks = participation.reduce((s, p) => s + p.linksGenerated, 0);
    const usedLinks = participation.reduce((s, p) => s + p.linksUsed, 0);
    const adherence = totalLinks > 0 ? usedLinks / totalLinks : null;

    const weighted = summary.stores.reduce(
      (acc, s) => ({ sum: acc.sum + s.overallRisk01 * s.responses, n: acc.n + s.responses }),
      { sum: 0, n: 0 },
    );
    const overallRisk = weighted.n > 0 ? weighted.sum / weighted.n : null;

    const withRisk = evolution.filter((p) => p.risk01 != null && p.responses > 0);
    const firstPoint = withRisk[0];
    const lastPoint = withRisk[withRisk.length - 1];
    const deltaPoints =
      firstPoint && lastPoint && firstPoint !== lastPoint
        ? Math.round((lastPoint.risk01 as number) * 100) - Math.round((firstPoint.risk01 as number) * 100)
        : null;

    const storesWithLinks = new Set(participation.filter((p) => p.linksGenerated > 0).map((p) => p.storeId));
    const universe = storeId ? 1 : stores.length;
    const withoutBatch = storeId ? 0 : stores.filter((s) => !storesWithLinks.has(s.id)).length;

    const months = evolution.filter((p) => p.responses > 0);
    const span =
      months.length > 0
        ? months.length === 1
          ? `${monthLabel(months[0].month, "long").toLowerCase()} de ${months[0].month.slice(0, 4)}`
          : `${monthLabel(months[0].month, "long").toLowerCase()} a ${monthLabel(months[months.length - 1].month, "long").toLowerCase()} de ${months[months.length - 1].month.slice(0, 4)}`
        : "";

    return {
      totalResponses,
      totalLinks,
      usedLinks,
      adherence,
      overallRisk,
      overallLevel: overallRisk == null ? null : levelOf(overallRisk),
      deltaPoints,
      deltaFrom: firstPoint?.month,
      universe,
      withoutBatch,
      span,
    };
  }, [summary, participation, evolution, stores, storeId]);

  const compareStore: Nr1StoreSummary | null = useMemo(
    () => summary.stores.find((s) => s.storeId === compareStoreId) ?? null,
    [summary, compareStoreId],
  );

  // ── Distribuição por pergunta de escala: desfavorável / neutro / favorável ─
  const likert = useMemo(
    () =>
      breakdown
        .filter((q) => q.type === "SCALE" && q.totalAnswers > 0 && q.distribution.length > 0)
        .map((q) => {
          const n = q.distribution.length;
          const mid = Math.floor(n / 2);
          const hasNeutral = n % 2 === 1;
          const total = q.distribution.reduce((s, d) => s + d.count, 0) || 1;
          const neg = q.distribution.slice(0, mid);
          const neu = hasNeutral ? [q.distribution[mid]] : [];
          const pos = q.distribution.slice(hasNeutral ? mid + 1 : mid);
          const share = (arr: { count: number }[]) => arr.reduce((s, d) => s + d.count, 0) / total;
          return {
            id: q.questionId,
            prompt: q.prompt,
            dimension: q.dimension,
            neg: share(neg),
            neu: share(neu),
            pos: share(pos),
            negLabels: neg.map((d) => d.label),
            neuLabels: neu.map((d) => d.label),
            posLabels: pos.map((d) => d.label),
          };
        }),
    [breakdown],
  );

  const joinLabels = (labels: string[]) =>
    labels.length <= 1 ? labels.join("") : `${labels.slice(0, -1).join(", ")} e ${labels[labels.length - 1]}`;

  // ── Evolução: geral + as duas dimensões mais críticas no último mês ────────
  const evolutionSeries = useMemo(() => {
    const points = evolution.filter((p) => p.responses > 0);
    if (points.length === 0) return { points, series: [] as { name: string; values: (number | null)[] }[] };
    const last = points[points.length - 1];
    const topDims = last.dimensions
      .filter((d) => d.risk01 != null)
      .sort((a, b) => (b.risk01 as number) - (a.risk01 as number))
      .slice(0, 2)
      .map((d) => d.dimension);
    const series = [
      { name: "Geral", values: points.map((p) => p.risk01) },
      ...topDims.map((dim) => ({
        name: dim,
        values: points.map((p) => p.dimensions.find((d) => d.dimension === dim)?.risk01 ?? null),
      })),
    ];
    return { points, series };
  }, [evolution]);

  // ── Adesão por loja ────────────────────────────────────────────────────────
  const adhesion = useMemo(
    () =>
      participation
        .filter((p) => p.linksGenerated > 0)
        .slice()
        .sort((a, b) => (b.participationRate ?? 0) - (a.participationRate ?? 0)),
    [participation],
  );

  /** "Risco mais alto + adesão mais baixa" na mesma loja merece uma frase. */
  const adhesionInsight = useMemo(() => {
    if (ranking.length < 2 || adhesion.length < 2) return null;
    const worstRisk = ranking[0];
    const worstAdhesion = adhesion[adhesion.length - 1];
    if (worstRisk.storeId !== worstAdhesion.storeId || worstAdhesion.participationRate == null) return null;
    return `${worstRisk.storeName} tem o maior risco e a menor adesão (${pct(worstAdhesion.participationRate)}): parte do time ainda não respondeu.`;
  }, [ranking, adhesion]);

  // ── Comentários anônimos ───────────────────────────────────────────────────
  const comments = useMemo(
    () =>
      breakdown
        .filter((q) => q.type === "TEXT")
        .flatMap((q) => (q.textAnswers ?? []).map((text, i) => ({ key: `${q.questionId}-${i}`, text, prompt: q.prompt }))),
    [breakdown],
  );

  if (!token) return null;

  const hasData = summary.stores.length > 0;
  const activeFilterCount = [formId, storeId, from, to].filter(Boolean).length;
  const formName = forms.find((f) => f.id === formId)?.title;
  const storeName = stores.find((s) => s.id === storeId)?.nomeFantasia;
  const shownLikert = showAllQuestions ? likert : likert.slice(0, PREVIEW_QUESTIONS);
  const shownComments = showAllComments ? comments : comments.slice(0, PREVIEW_COMMENTS);

  return (
    <div className="nr1Page">
      <main className="nr1Page__main">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate(HOMEPAGE()) },
            { label: hub.label, onClick: () => navigate(hub.to) },
            { label: "NR1, riscos por loja" },
          ]}
          title="Riscos psicossociais por loja"
          subtitle="Respostas anônimas agrupadas por dimensão de risco. Risco de 0 a 100%: até 33% baixo, até 66% médio, acima disso alto."
          onBack={goBack}
          backLabel={`Voltar para ${hub.label}`}
          actions={
            <button type="button" className="ecoBtn" onClick={() => navigate(NR1_LINKS_ADMIN_PAGE())}>
              <EcoIcon name="link" size={18} />
              Gerar links
            </button>
          }
        />

        <EcoFiltersCard
          activeCount={activeFilterCount}
          summary={`${formName ?? "Todos os formulários"} · ${storeName ?? "Todas as lojas"}`}
        >
          <div className="ecoFilters__grid">
            <div className="ecoField">
              <label htmlFor="d-form">Formulário</label>
              <select id="d-form" className="ecoSelect ecoSelect--sm" value={formId} onChange={(e) => setFormId(e.target.value)}>
                <option value="">Todos os formulários</option>
                {forms.map((f) => (
                  <option key={f.id} value={f.id}>{f.title}</option>
                ))}
              </select>
            </div>
            <div className="ecoField">
              <label htmlFor="d-loja">Loja</label>
              <select id="d-loja" className="ecoSelect ecoSelect--sm" value={storeId} onChange={(e) => setStoreId(e.target.value)}>
                <option value="">Todas as lojas</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>{s.nomeFantasia}</option>
                ))}
              </select>
            </div>
            <div className="ecoField">
              <label htmlFor="d-de">De</label>
              <input id="d-de" className="ecoInput ecoInput--sm" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="ecoField">
              <label htmlFor="d-ate">Até</label>
              <input id="d-ate" className="ecoInput ecoInput--sm" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>
          <div className="ecoFilters__row">
            <button type="button" className="ecoBtn ecoFilters__clear" onClick={handleClear}>
              Limpar
            </button>
          </div>
        </EcoFiltersCard>

        {loading ? (
          <p className="nr1Page__loading" role="status">Carregando relatórios...</p>
        ) : !hasData ? (
          <section className="ecoCard nr1Panel">
            <p className="ecoEmpty">
              Ainda não há respostas para os filtros escolhidos. Gere links em{" "}
              <button type="button" className="ecoLinkBtn" onClick={() => navigate(NR1_LINKS_ADMIN_PAGE())}>
                Links anônimos
              </button>{" "}
              e aguarde as respostas.
            </p>
          </section>
        ) : (
          <>
            {/* Indicadores */}
            <section className="nr1Kpis" aria-label="Indicadores">
              <div className="ecoCard nr1Kpi">
                <span className="nr1Kpi__label">Respostas anônimas</span>
                <span className="nr1Kpi__value">{kpis.totalResponses}</span>
                <span className="nr1Kpi__note">{kpis.span || "no filtro"}</span>
              </div>
              <div className="ecoCard nr1Kpi">
                <span className="nr1Kpi__label">Lojas com respostas</span>
                <span className="nr1Kpi__value">
                  {summary.stores.length} <span className="nr1Kpi__of">de {kpis.universe}</span>
                </span>
                <span className="nr1Kpi__note">
                  {kpis.withoutBatch > 0
                    ? `${kpis.withoutBatch} ${kpis.withoutBatch === 1 ? "loja ainda sem lote gerado" : "lojas ainda sem lote gerado"}`
                    : "todas com lote gerado"}
                </span>
              </div>
              <div className="ecoCard nr1Kpi">
                <span className="nr1Kpi__label">Adesão média</span>
                <span className="nr1Kpi__value">{pct(kpis.adherence)}</span>
                <span className="nr1Kpi__note">
                  {kpis.usedLinks} de {kpis.totalLinks} links respondidos
                </span>
              </div>
              <div className="ecoCard nr1Kpi">
                <span className="nr1Kpi__label">Risco geral</span>
                <span className="nr1Kpi__valueRow">
                  <span className="nr1Kpi__value">{pct(kpis.overallRisk)}</span>
                  {kpis.overallLevel ? <LevelPill level={kpis.overallLevel} /> : null}
                </span>
                <span className="nr1Kpi__note">
                  {kpis.deltaPoints === null || !kpis.deltaFrom ? (
                    "média ponderada pelas respostas"
                  ) : kpis.deltaPoints === 0 ? (
                    `igual a ${monthLabel(kpis.deltaFrom, "long").toLowerCase()}`
                  ) : (
                    <>
                      <EcoIcon name={kpis.deltaPoints < 0 ? "arrowDown" : "arrowUp"} size={14} strokeWidth={2.4} />
                      {Math.abs(kpis.deltaPoints)} {Math.abs(kpis.deltaPoints) === 1 ? "ponto" : "pontos"}{" "}
                      {kpis.deltaPoints < 0 ? "abaixo" : "acima"} de {monthLabel(kpis.deltaFrom, "long").toLowerCase()}
                    </>
                  )}
                </span>
              </div>
            </section>

            {/* Mapa de risco */}
            <section className="ecoCard nr1Panel nr1Panel--flush nr1Desktop" aria-labelledby="nr1-heat">
              <div className="nr1Panel__head">
                <div>
                  <h2 id="nr1-heat" className="ecoCard__title">Mapa de risco, loja por dimensão</h2>
                  <p className="ecoCard__subtitle">
                    Quanto mais escuro, maior o risco. Passe o mouse para ver a faixa e quantas respostas sustentam o número.
                  </p>
                </div>
                <div className="nr1Scale" aria-hidden="true">
                  <span>0%</span>
                  <span className="nr1Scale__steps">
                    {[0, 0.25, 0.5, 0.75, 1].map((v) => (
                      <span key={v} style={{ background: riskCellColor(v).bg }} />
                    ))}
                  </span>
                  <span>100%</span>
                </div>
              </div>
              <div className="ecoTableWrap">
                <table className="ecoTable nr1Heat">
                  <thead>
                    <tr>
                      <th scope="col">Loja</th>
                      {summary.dimensions.map((d) => (
                        <th key={d} scope="col" className="nr1Heat__dim">{d}</th>
                      ))}
                      <th scope="col">Geral</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ranking.map((s) => (
                      <tr key={s.storeId}>
                        <th scope="row" className="nr1Heat__store">
                          {s.storeName}
                          <span className="nr1Heat__responses">
                            {s.responses} {s.responses === 1 ? "resposta" : "respostas"}
                          </span>
                        </th>
                        {summary.dimensions.map((dim) => {
                          const d = s.dimensions.find((x) => x.dimension === dim);
                          if (!d) return <td key={dim} className="nr1Heat__cell nr1Heat__cell--empty">—</td>;
                          const c = riskCellColor(d.risk01);
                          const label = `${s.storeName}, ${dim}: risco ${pct(d.risk01)} (${(LEVEL_LABEL[d.riskLevel] ?? d.riskLevel).toLowerCase()}), ${d.answersCount} respostas`;
                          return (
                            <td key={dim} className="nr1Heat__cell">
                              <span className="nr1Heat__swatch" style={{ background: c.bg, color: c.ink }} title={label} aria-label={label}>
                                {pct(d.risk01)}
                              </span>
                            </td>
                          );
                        })}
                        <td className="nr1Heat__overall">
                          <strong>{pct(s.overallRisk01)}</strong> <LevelPill level={s.overallRiskLevel} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <div className="nr1Row">
              {/* Ranking */}
              <section className="ecoCard nr1Panel" aria-labelledby="nr1-rank">
                <div>
                  <h2 id="nr1-rank" className="ecoCard__title">Ranking de risco por loja</h2>
                  <p className="ecoCard__subtitle">Da mais crítica para a menos crítica. A cor indica a faixa.</p>
                </div>
                <ul className="nr1Bars">
                  {ranking.map((s) => {
                    const worst = s.dimensions.slice().sort((a, b) => b.risk01 - a.risk01)[0];
                    const part = participation.find((p) => p.storeId === s.storeId);
                    return (
                      <li key={s.storeId} className="nr1Bars__item">
                        <div className="nr1Bars__line">
                          <span className="nr1Bars__name">{s.storeName}</span>
                          <span className="nr1Bars__value">
                            <strong>{pct(s.overallRisk01)}</strong> <LevelPill level={s.overallRiskLevel} />
                          </span>
                        </div>
                        <span className="nr1Bars__track" aria-hidden="true">
                          <span
                            className={`nr1Bars__fill nr1Bars__fill--${LEVEL_CLASS[s.overallRiskLevel] ?? "warn"}`}
                            style={{ width: `${Math.round(s.overallRisk01 * 100)}%` }}
                          />
                        </span>
                        <span className="nr1Bars__meta">
                          {worst ? `Pior dimensão: ${worst.dimension}, ${pct(worst.risk01)}` : ""}
                          {part?.participationRate != null ? ` · adesão ${pct(part.participationRate)}` : ""}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <div className="nr1Legend">
                  <span><span className="nr1Dot nr1Dot--good" aria-hidden="true" />Baixo, até 33%</span>
                  <span><span className="nr1Dot nr1Dot--warn" aria-hidden="true" />Médio, até 66%</span>
                  <span><span className="nr1Dot nr1Dot--bad" aria-hidden="true" />Alto, acima de 66%</span>
                </div>
              </section>

              {/* Loja x média */}
              <section className="ecoCard nr1Panel nr1Desktop" aria-labelledby="nr1-compare">
                <div className="nr1Panel__headInline">
                  <div>
                    <h2 id="nr1-compare" className="ecoCard__title">
                      {compareStore ? `${compareStore.storeName} em comparação com a média` : "Loja em comparação com a média"}
                    </h2>
                    <p className="ecoCard__subtitle">Risco por dimensão da loja escolhida contra a média de todas as lojas.</p>
                  </div>
                  <label htmlFor="nr1-compare-store" className="ecoVisuallyHidden">Loja para comparar</label>
                  <select
                    id="nr1-compare-store"
                    className="ecoSelect ecoSelect--sm nr1Compare__select"
                    value={compareStoreId}
                    onChange={(e) => setCompareStoreId(e.target.value)}
                  >
                    {ranking.map((s) => (
                      <option key={s.storeId} value={s.storeId}>{s.storeName}</option>
                    ))}
                  </select>
                </div>
                {compareStore ? (
                  <>
                    <ul className="nr1Compare">
                      {summary.dimensions.map((dim) => {
                        const own = compareStore.dimensions.find((d) => d.dimension === dim)?.risk01 ?? null;
                        const avg = dimensionAverages[dim];
                        return (
                          <li key={dim} className="nr1Compare__item">
                            <span className="nr1Compare__dim">{dim}</span>
                            <span className="nr1Compare__bars" aria-hidden="true">
                              <span className="nr1Compare__track">
                                <span className="nr1Compare__fill nr1Compare__fill--store" style={{ width: `${Math.round((own ?? 0) * 100)}%` }} />
                              </span>
                              <span className="nr1Compare__track">
                                <span className="nr1Compare__fill nr1Compare__fill--avg" style={{ width: `${Math.round((avg ?? 0) * 100)}%` }} />
                              </span>
                            </span>
                            <span className="nr1Compare__values">
                              <strong>{pct(own)}</strong> <span>vs {pct(avg)}</span>
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                    <div className="nr1Legend">
                      <span><span className="nr1Dot nr1Dot--store" aria-hidden="true" />{compareStore.storeName}</span>
                      <span><span className="nr1Dot nr1Dot--avg" aria-hidden="true" />Média das lojas</span>
                    </div>
                  </>
                ) : null}
              </section>
            </div>

            {/* Risco por dimensão (celular, no lugar do mapa) */}
            <section className="ecoCard nr1Panel nr1Mobile" aria-labelledby="nr1-dims">
              <div>
                <h2 id="nr1-dims" className="ecoCard__title">Risco por dimensão</h2>
                <p className="ecoCard__subtitle">Média de todas as lojas no filtro.</p>
              </div>
              <ul className="nr1Bars">
                {summary.dimensions
                  .slice()
                  .sort((a, b) => (dimensionAverages[b] ?? 0) - (dimensionAverages[a] ?? 0))
                  .map((dim) => {
                    const v = dimensionAverages[dim];
                    return (
                      <li key={dim} className="nr1Bars__item">
                        <div className="nr1Bars__line">
                          <span>{dim}</span>
                          <strong>{pct(v)}</strong>
                        </div>
                        <span className="nr1Bars__track" aria-hidden="true">
                          <span
                            className={`nr1Bars__fill nr1Bars__fill--${LEVEL_CLASS[levelOf(v ?? 0)]}`}
                            style={{ width: `${Math.round((v ?? 0) * 100)}%` }}
                          />
                        </span>
                      </li>
                    );
                  })}
              </ul>
            </section>

            {/* Distribuição por pergunta */}
            {likert.length > 0 ? (
              <section className="ecoCard nr1Panel nr1Desktop" aria-labelledby="nr1-likert">
                <div className="nr1Panel__headInline">
                  <div>
                    <h2 id="nr1-likert" className="ecoCard__title">Distribuição das respostas por pergunta</h2>
                    <p className="ecoCard__subtitle">
                      Como as notas se dividiram em cada pergunta de escala. Opiniões divididas e consenso morno aparecem de formas diferentes.
                    </p>
                  </div>
                  <div className="nr1Legend">
                    <span><span className="nr1Dot nr1Dot--neg" aria-hidden="true" />Desfavorável{likert[0].negLabels.length ? `, notas ${joinLabels(likert[0].negLabels)}` : ""}</span>
                    {likert[0].neuLabels.length ? (
                      <span><span className="nr1Dot nr1Dot--neu" aria-hidden="true" />Neutro, nota {likert[0].neuLabels[0]}</span>
                    ) : null}
                    <span><span className="nr1Dot nr1Dot--pos" aria-hidden="true" />Favorável{likert[0].posLabels.length ? `, notas ${joinLabels(likert[0].posLabels)}` : ""}</span>
                  </div>
                </div>
                <ul className="nr1Likert">
                  {shownLikert.map((q) => (
                    <li key={q.id} className="nr1Likert__item">
                      <span className="nr1Likert__prompt">
                        {q.dimension ? <span className="nr1Likert__dim">{q.dimension}</span> : null}
                        {q.prompt}
                      </span>
                      <span className="nr1Likert__bar">
                        {([
                          ["neg", "Desfavorável", q.neg],
                          ["neu", "Neutro", q.neu],
                          ["pos", "Favorável", q.pos],
                        ] as [string, string, number][])
                          .filter(([, , v]) => v > 0)
                          .map(([k, label, v]) => (
                            <span
                              key={k}
                              className={`nr1Likert__seg nr1Likert__seg--${k}`}
                              style={{ width: `${v * 100}%` }}
                              title={`${label}: ${pct(v)}`}
                            >
                              {v >= 0.1 ? pct(v) : ""}
                              <span className="ecoVisuallyHidden"> {label}</span>
                            </span>
                          ))}
                      </span>
                    </li>
                  ))}
                </ul>
                {likert.length > PREVIEW_QUESTIONS ? (
                  <p className="nr1Panel__more">
                    {showAllQuestions ? "" : `Mostrando ${PREVIEW_QUESTIONS} de ${likert.length} perguntas de escala. `}
                    <button type="button" className="ecoLinkBtn" onClick={() => setShowAllQuestions((v) => !v)}>
                      {showAllQuestions ? "Mostrar menos" : "Ver todas"}
                    </button>
                  </p>
                ) : null}
              </section>
            ) : null}

            <div className="nr1Row">
              {/* Evolução mensal */}
              {evolutionSeries.points.length > 0 ? (
                <section className="ecoCard nr1Panel nr1Desktop" aria-labelledby="nr1-evo">
                  <div>
                    <h2 id="nr1-evo" className="ecoCard__title">Evolução mensal do risco</h2>
                    <p className="ecoCard__subtitle">
                      Risco geral e as {evolutionSeries.series.length - 1 === 1 ? "dimensão mais crítica" : "duas dimensões mais críticas"} no último mês.
                    </p>
                  </div>
                  <EvolutionChart points={evolutionSeries.points.map((p) => p.month)} series={evolutionSeries.series} />
                  <div className="nr1Legend">
                    {evolutionSeries.series.map((s, i) => (
                      <span key={s.name}>
                        <span className="nr1Line" style={{ background: EVOLUTION_COLORS[i] }} aria-hidden="true" />
                        {s.name}
                      </span>
                    ))}
                  </div>
                </section>
              ) : null}

              {/* Adesão */}
              {adhesion.length > 0 ? (
                <section className="ecoCard nr1Panel" aria-labelledby="nr1-adh">
                  <div>
                    <h2 id="nr1-adh" className="ecoCard__title">Adesão por loja</h2>
                    <p className="ecoCard__subtitle">
                      Links respondidos sobre links gerados. Lotes revogados ficam de fora. Risco baixo com pouca adesão não é conclusivo.
                    </p>
                  </div>
                  <ul className="nr1Bars">
                    {adhesion.map((p) => (
                      <li key={p.storeId} className="nr1Bars__item">
                        <div className="nr1Bars__line">
                          <span className="nr1Bars__name">{p.storeName}</span>
                          <span className="nr1Bars__value">
                            <strong>{pct(p.participationRate)}</strong>{" "}
                            <span className="nr1Bars__meta">{p.linksUsed}/{p.linksGenerated}</span>
                          </span>
                        </div>
                        <span className="nr1Bars__track" aria-hidden="true">
                          <span className="nr1Bars__fill nr1Bars__fill--primary" style={{ width: `${Math.round((p.participationRate ?? 0) * 100)}%` }} />
                        </span>
                      </li>
                    ))}
                  </ul>
                  {adhesionInsight ? <p className="nr1Insight">{adhesionInsight}</p> : null}
                </section>
              ) : null}
            </div>

            {/* Comentários */}
            {comments.length > 0 ? (
              <section className="ecoCard nr1Panel" aria-labelledby="nr1-comments">
                <div className="nr1Panel__headInline">
                  <div>
                    <h2 id="nr1-comments" className="ecoCard__title">Comentários anônimos</h2>
                    <p className="ecoCard__subtitle">Respostas de texto livre, sem nenhuma identificação de quem escreveu.</p>
                  </div>
                  <span className="nr1Panel__count">
                    {comments.length} {comments.length === 1 ? "comentário" : "comentários"} no filtro
                  </span>
                </div>
                <div className="nr1Comments">
                  {shownComments.map((c) => (
                    <blockquote key={c.key} className="nr1Comment">
                      <p>"{c.text}"</p>
                      <footer>{c.prompt}</footer>
                    </blockquote>
                  ))}
                </div>
                {comments.length > PREVIEW_COMMENTS ? (
                  <button type="button" className="ecoLinkBtn nr1Comments__more" onClick={() => setShowAllComments((v) => !v)}>
                    {showAllComments ? "Mostrar menos" : `Ler todos os ${comments.length} comentários`}
                  </button>
                ) : null}
              </section>
            ) : null}

            <p className="nr1Page__mobileNote">
              Mapa de risco por dimensão, comparação com a média, distribuição por pergunta e evolução mensal ficam na versão para computador.
            </p>
          </>
        )}
      </main>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Gráfico de linhas (SVG) — eixo único 0–100%, rótulo direto no último ponto,
// coluna de hover/foco por mês com tooltip.
// ---------------------------------------------------------------------------

const CHART_W = 640;
const CHART_H = 230;
const PAD = { left: 40, right: 48, top: 20, bottom: 30 };

const EvolutionChart: React.FC<{
  points: string[];
  series: { name: string; values: (number | null)[] }[];
}> = ({ points, series }) => {
  const [hover, setHover] = useState<number | null>(null);
  const plotW = CHART_W - PAD.left - PAD.right;
  const plotH = CHART_H - PAD.top - PAD.bottom;
  const x = (i: number) => PAD.left + (points.length === 1 ? plotW / 2 : (i / (points.length - 1)) * plotW);
  const y = (v: number) => PAD.top + (1 - v) * plotH;
  const colW = points.length > 1 ? plotW / (points.length - 1) : plotW;

  // Rótulos diretos no fim, afastados para não colidir.
  const endLabels = series
    .map((s, i) => {
      const lastIdx = s.values.map((v) => v != null).lastIndexOf(true);
      return lastIdx >= 0 ? { i, v: s.values[lastIdx] as number, idx: lastIdx } : null;
    })
    .filter(Boolean) as { i: number; v: number; idx: number }[];
  const placed = endLabels
    .slice()
    .sort((a, b) => y(a.v) - y(b.v))
    .reduce<{ i: number; v: number; idx: number; ly: number }[]>((acc, l) => {
      const prev = acc[acc.length - 1];
      const ly = Math.max(y(l.v) + 4, prev ? prev.ly + 14 : -Infinity);
      return [...acc, { ...l, ly }];
    }, []);

  return (
    <figure className="nr1Chart">
      <svg
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        width="100%"
        role="img"
        aria-label={`Risco mensal: ${series
          .map((s) => {
            const vals = s.values.filter((v) => v != null) as number[];
            return vals.length ? `${s.name} de ${pct(vals[0])} para ${pct(vals[vals.length - 1])}` : s.name;
          })
          .join("; ")}`}
        onMouseLeave={() => setHover(null)}
      >
        {[1, 0.5, 0].map((v) => (
          <g key={v}>
            <line x1={PAD.left} x2={CHART_W - PAD.right} y1={y(v)} y2={y(v)} stroke={v === 0 ? "#d5ded7" : "#ecf1ed"} strokeWidth={1} />
            <text x={PAD.left - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill="#8c9a90">
              {v === 0 ? "0" : `${v * 100}%`}
            </text>
          </g>
        ))}

        {hover != null ? (
          <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + plotH} stroke="#b9cbbe" strokeWidth={1} />
        ) : null}

        {series.map((s, si) => {
          const pts = s.values
            .map((v, i) => (v == null ? null : `${x(i)},${y(v)}`))
            .filter(Boolean)
            .join(" ");
          return (
            <polyline
              key={s.name}
              points={pts}
              fill="none"
              stroke={EVOLUTION_COLORS[si]}
              strokeWidth={si === 0 ? 2.5 : 2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          );
        })}

        {series.map((s, si) =>
          s.values.map((v, i) =>
            v == null || (hover !== i && i !== s.values.length - 1) ? null : (
              <circle key={`${s.name}-${i}`} cx={x(i)} cy={y(v)} r={4} fill={EVOLUTION_COLORS[si]} stroke="#ffffff" strokeWidth={2} />
            ),
          ),
        )}

        {placed.map((l) => (
          <text key={l.i} x={x(l.idx) + 8} y={l.ly} fontSize="12" fontWeight="700" fill="#0e1f14">
            {pct(l.v)}
          </text>
        ))}

        {points.map((m, i) => (
          <text key={m} x={x(i)} y={CHART_H - 8} textAnchor="middle" fontSize="11" fill="#55675b">
            {monthLabel(m, "short")}
          </text>
        ))}

        {/* Alvos de hover maiores que as marcas */}
        {points.map((m, i) => (
          <rect
            key={`hit-${m}`}
            x={x(i) - colW / 2}
            y={PAD.top}
            width={colW}
            height={plotH}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
            onFocus={() => setHover(i)}
            onBlur={() => setHover(null)}
            tabIndex={0}
            aria-label={`${monthLabel(m, "long")}: ${series.map((s) => `${s.name} ${pct(s.values[i])}`).join(", ")}`}
          />
        ))}
      </svg>

      {hover != null ? (
        <div
          className="nr1Chart__tip"
          style={{ left: `${(x(hover) / CHART_W) * 100}%` }}
          role="tooltip"
        >
          <strong>{monthLabel(points[hover], "long")} de {points[hover].slice(0, 4)}</strong>
          {series.map((s, si) => (
            <span key={s.name}>
              <span className="nr1Line" style={{ background: EVOLUTION_COLORS[si] }} aria-hidden="true" />
              {s.name}: {pct(s.values[hover])}
            </span>
          ))}
        </div>
      ) : null}
    </figure>
  );
};

export default Nr1DashboardPage;
